import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Goal, GoalStatus } from '../types/Goal';
import { goalService } from '../services/goalService';
import { useAuth } from '../contexts/AuthContext';
import './Timeline.css';

interface TimelineProps {
  className?: string;
  onToggleSidebar?: () => void;
}

type ViewMode = 'monthly' | 'yearly';

const Timeline: React.FC<TimelineProps> = ({ className = '', onToggleSidebar }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const savedViewMode = localStorage.getItem('timeline-view-mode');
    return (savedViewMode === 'yearly' || savedViewMode === 'monthly') ? savedViewMode : 'monthly';
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Generate months for the timeline (6 months before and after current month)
  const generateMonths = (centerMonth: Date) => {
    const months = [];
    for (let i = -6; i <= 12; i++) {
      const month = new Date(centerMonth.getFullYear(), centerMonth.getMonth() + i, 1);
      months.push(month);
    }
    return months;
  };

  // Generate years for the timeline (2 years before and after current year)
  const generateYears = (centerYear: number) => {
    const years = [];
    for (let i = -2; i <= 5; i++) {
      years.push(centerYear + i);
    }
    return years;
  };

  const [months] = useState(() => generateMonths(new Date()));
  const [years] = useState(() => generateYears(new Date().getFullYear()));

  useEffect(() => {
    if (user) {
      fetchActiveGoals();
    }
  }, [user]);

  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('timeline-view-mode', viewMode);
  }, [viewMode]);

  const fetchActiveGoals = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const response = await goalService.getUserGoals({
        user_id: user.id.toString(),
        limit: 1000 // Fetch all goals
      });

      if (response.success && response.goals) {
        setGoals(response.goals.map(goal => goalService.convertFirebaseGoal(goal)));
      }
    } catch (err) {
      console.error('Error fetching goals:', err);
      setError('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const getYearLabel = (year: number) => {
    return year.toString();
  };

  const getQuarterLabel = (year: number, quarter: number) => {
    return `Q${quarter} ${year}`;
  };

  const getGoalDuration = (goal: Goal) => {
    // Use startedDate if available, fallback to createdDate
    const startDate = new Date(goal.startedDate || goal.createdDate);
    const endDate = goal.targetDate ? new Date(goal.targetDate) : new Date();
    
    // If no target date, assume 6 months duration
    if (!goal.targetDate) {
      endDate.setMonth(startDate.getMonth() + 6);
    }

    return { startDate, endDate };
  };

  const getGoalPosition = (goal: Goal) => {
    const { startDate, endDate } = getGoalDuration(goal);
    
    if (viewMode === 'monthly') {
      const firstMonth = months[0];
      const lastMonth = months[months.length - 1];

      // Calculate position as percentage
      const timelineStart = firstMonth.getTime();
      const timelineEnd = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0).getTime();
      const goalStart = Math.max(startDate.getTime(), timelineStart);
      const goalEnd = Math.min(endDate.getTime(), timelineEnd);

      const leftPercent = ((goalStart - timelineStart) / (timelineEnd - timelineStart)) * 100;
      const widthPercent = ((goalEnd - goalStart) / (timelineEnd - timelineStart)) * 100;

      return {
        left: `${Math.max(0, leftPercent)}%`,
        width: `${Math.max(1, widthPercent)}%`
      };
    } else {
      // Yearly view
      const firstYear = years[0];
      const lastYear = years[years.length - 1];
      
      const timelineStart = new Date(firstYear, 0, 1).getTime();
      const timelineEnd = new Date(lastYear + 1, 0, 1).getTime();
      const goalStart = Math.max(startDate.getTime(), timelineStart);
      const goalEnd = Math.min(endDate.getTime(), timelineEnd);

      const leftPercent = ((goalStart - timelineStart) / (timelineEnd - timelineStart)) * 100;
      const widthPercent = ((goalEnd - goalStart) / (timelineEnd - timelineStart)) * 100;

      return {
        left: `${Math.max(0, leftPercent)}%`,
        width: `${Math.max(1, widthPercent)}%`
      };
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      personal: '#666666',
      career: '#333333',
      health: '#555555',
      education: '#444444',
      finance: '#777777',
      relationships: '#888888',
      hobbies: '#999999',
      travel: '#aaaaaa',
      other: '#bbbbbb'
    };
    return colors[category] || '#666666';
  };

  const getGoalOpacity = (goal: Goal) => {
    switch (goal.status) {
      case 'completed':
        return 0.6;
      case 'on_hold':
        return 0.4;
      case 'cancelled':
        return 0.3;
      case 'not_started':
        return 0.7;
      case 'in_progress':
      default:
        return 1.0;
    }
  };

  const scrollToCurrentPeriod = () => {
    const headerElement = document.querySelector('.gantt-header') as HTMLElement;
    const contentElement = document.querySelector('.gantt-content') as HTMLElement;
    
    if (headerElement && contentElement) {
      if (viewMode === 'monthly') {
        const currentMonthIndex = months.findIndex(
          month => month.getMonth() === new Date().getMonth() && 
                   month.getFullYear() === new Date().getFullYear()
        );
        
        if (currentMonthIndex !== -1) {
          const monthWidth = 120; // Approximate width of each month column
          const scrollPosition = Math.max(0, (currentMonthIndex - 2) * monthWidth);
          headerElement.scrollLeft = scrollPosition;
          contentElement.scrollLeft = scrollPosition;
        }
      } else {
        const currentYearIndex = years.findIndex(year => year === new Date().getFullYear());
        
        if (currentYearIndex !== -1) {
          const yearWidth = 240; // Approximate width of each year column
          const scrollPosition = Math.max(0, (currentYearIndex - 1) * yearWidth);
          headerElement.scrollLeft = scrollPosition;
          contentElement.scrollLeft = scrollPosition;
        }
      }
    }
  };

  // Synchronize scrolling between header and content
  const handleHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const contentElement = document.querySelector('.gantt-content') as HTMLElement;
    if (contentElement) {
      contentElement.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const headerElement = document.querySelector('.gantt-header') as HTMLElement;
    if (headerElement) {
      headerElement.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleGoalClick = (goal: Goal) => {
    navigate(`/goals/${goal.id}`);
  };

  useEffect(() => {
    // Scroll to current period on component mount or view change
    const timer = setTimeout(scrollToCurrentPeriod, 100);
    return () => clearTimeout(timer);
  }, [months, years, viewMode]);

  if (loading) {
    return (
      <div className={`timeline-container ${className}`}>
        <div className="timeline-header">
          <h1>Timeline</h1>
        </div>
        <div className="timeline-loading">
          <div className="loading-spinner"></div>
          <p>Loading your goals...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`timeline-container ${className}`}>
        <div className="timeline-header">
          <h1>Timeline</h1>
        </div>
        <div className="timeline-error">
          <p>{error}</p>
          <button onClick={fetchActiveGoals} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`timeline-container ${className}`}>
      <div className="timeline-header">
        <div className="timeline-header-left">
          {onToggleSidebar && (
            <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
          )}
          <h1>Timeline</h1>
          <div className="view-toggle">
            <button 
              className={`view-btn ${viewMode === 'monthly' ? 'active' : ''}`}
              onClick={() => setViewMode('monthly')}
            >
              Monthly
            </button>
            <button 
              className={`view-btn ${viewMode === 'yearly' ? 'active' : ''}`}
              onClick={() => setViewMode('yearly')}
            >
              Yearly
            </button>
          </div>
        </div>
        <div className="timeline-controls">
          <button onClick={scrollToCurrentPeriod} className="current-period-btn">
            Go to Current {viewMode === 'monthly' ? 'Month' : 'Year'}
          </button>
          <div className="goal-count">
            {goals.length} goal{goals.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div className="gantt-container">
        <div className="gantt-header" onScroll={handleHeaderScroll}>
          {viewMode === 'monthly' ? (
            <div className="timeline-months">
              {months.map((month, index) => (
                <div 
                  key={index} 
                  className={`month-header ${
                    month.getMonth() === new Date().getMonth() && 
                    month.getFullYear() === new Date().getFullYear() 
                      ? 'current-month' 
                      : ''
                  }`}
                >
                  {getMonthName(month)}
                </div>
              ))}
            </div>
          ) : (
            <div className="timeline-years">
              {years.map((year, index) => (
                <div 
                  key={index} 
                  className={`year-header ${
                    year === new Date().getFullYear() 
                      ? 'current-year' 
                      : ''
                  }`}
                >
                  <div className="year-label">{getYearLabel(year)}</div>
                  <div className="quarter-labels">
                    {[1, 2, 3, 4].map(quarter => (
                      <div key={quarter} className="quarter-label">
                        Q{quarter}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="gantt-content" onScroll={handleContentScroll}>
          {goals.length === 0 ? (
              <div className="no-goals" style={{ minWidth: viewMode === 'monthly' ? 'calc(120px * 19)' : 'calc(240px * 8)' }}>
                <p>No goals found</p>
                <p className="no-goals-subtitle">Create some goals to visualize them here</p>
              </div>
          ) : (
            <div 
              className={`timeline-grid ${viewMode}`}
              style={{ minHeight: `${Math.max(400, goals.length * 50 + 40)}px` }}
            >
              {/* Grid lines */}
              <div className={viewMode === 'monthly' ? 'month-grid' : 'year-grid'}>
                {viewMode === 'monthly' ? (
                  months.map((month, index) => (
                    <div key={index} className="month-column"></div>
                  ))
                ) : (
                  years.map((year, index) => (
                    <div key={index} className="year-column">
                      {[1, 2, 3, 4].map(quarter => (
                        <div key={quarter} className="quarter-column"></div>
                      ))}
                    </div>
                  ))
                )}
              </div>

              {/* Goal bars */}
              <div className="goal-bars">
                {goals.map((goal, index) => {
                  const position = getGoalPosition(goal);
                  return (
                    <div
                      key={goal.id}
                      className="goal-bar"
                      style={{
                        ...position,
                        backgroundColor: getCategoryColor(goal.category),
                        opacity: getGoalOpacity(goal),
                        top: `${index * 50 + 20}px`,
                      }}
                      title={`${goal.title} (${goal.progress}% complete, ${goal.status.replace('_', ' ')}) - Click to view details`}
                      onClick={() => handleGoalClick(goal)}
                    >
                      <div className="goal-bar-progress" style={{ width: `${goal.progress}%` }}></div>
                      <div className="goal-bar-label">{goal.title}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Timeline;
